import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import {
  faCalendar,
  faTrashCan,
  faUserPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from 'urql';
import { FindAddContact } from '~/components/FindAddContact/FindAddContact';
import { Input } from '~/components/form/TextField';
import { TimePicker } from '~/components/form/TimePicker';
import { getJobDateKey } from '~/components/job/formatDate';
import { getDefaultScheduledEndTime } from '~/components/job/getDefaultScheduledEndTime';
import { DateDisplay } from '~/components/sites/tabs/DateDisplay';
import { JobsTabSkeleton } from '~/components/skeletons';
import { CardSmall } from '~/components/ui/CardSmall';
import { Confirm } from '~/components/ui/Confirm';
import { Loading } from '~/components/ui/Loading';
import { PopupDialog } from '~/components/ui/PopupDialog';
import { IconButton } from '~/components/ui/nucleus/Button/IconButton';
import { JobStatus, Status, useCancelJobMutation } from '~/generated/graphql';
import {
  AssetJobsRouteDocument,
  AssignJobsDocument,
  RescheduleJobsDocument,
} from '~/gql/graphql';
import { formatTime } from '~/helpers/formatDate';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import type { Contact } from '~/routes/resources/contacts';

type AssetJob = {
  id: string;
  status: Status | JobStatus;
  name: string;
  _dateScheduled?: string | null;
  scheduleStart?: string | null;
  scheduleEnd?: string | null;
  scheduledStartDate?: string | null;
  scheduledStartTime?: string | null;
  scheduledEndDate?: string | null;
  scheduledEndTime?: string | null;
  scheduledStartStrict?: boolean | null;
  scheduledEndStrict?: boolean | null;
  timeZone?: string | null;
  location?: {
    __typename?: string;
    id: string;
    name: string;
  } | null;
  assignee?: {
    id: string;
    name: string;
  } | null;
};

export function useAssetJobs(assetId: string, enabled = true) {
  const [result, reexecuteQuery] = useQuery({
    query: AssetJobsRouteDocument,
    variables: { id: assetId },
    requestPolicy: 'cache-first',
    pause: !assetId || !enabled,
  });

  const assetJobs = (result.data?.asset?.jobs ?? []) as AssetJob[];
  return [
    assetJobs,
    () => reexecuteQuery({ requestPolicy: 'network-only' }),
    result.fetching,
  ] as [AssetJob[], () => void, boolean];
}

export function AssetJobs({
  assetId,
  highlightedJobId,
  showSkeleton,
  onHighlightedJobClick,
}: {
  assetId: string;
  highlightedJobId?: string | null;
  showSkeleton: boolean;
  onHighlightedJobClick?: (jobId: string) => void;
}) {
  const [jobs, , fetching] = useAssetJobs(assetId);
  const jobRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());

  // Simple scroll function without complex retry logic
  const scrollToJob = useCallback((jobId: string) => {
    const jobElement = jobRefsMap.current.get(jobId);
    if (jobElement) {
      jobElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, []);

  // Scroll to highlighted job when available
  useEffect(() => {
    if (highlightedJobId && jobs.length > 0) {
      // Small delay to ensure DOM is ready
      const scrollTimeout = setTimeout(
        () => scrollToJob(highlightedJobId),
        100
      );

      // Cleanup timeout on effect cleanup
      return () => clearTimeout(scrollTimeout);
    }
  }, [highlightedJobId, jobs, scrollToJob]);

  // Memoize sorted jobs to prevent recreation on every render
  // Filter out cancelled and completed jobs, then sort appropriately
  const sortedJobs = useMemo(() => {
    return [...jobs]
      .filter((job) => {
        // Always filter out cancelled jobs
        if (job.status === JobStatus.Cancelled) {
          return false;
        }

        // Always filter out completed jobs - they should only appear in the history tab
        // Complete status only - Incomplete means "Deferred" and should be shown
        if (job.status === JobStatus.Complete) {
          return false;
        }

        // Show all non-cancelled, non-completed jobs
        return true;
      })
      .sort((a, b) => {
        const aDateKey =
          getJobDateKey(a, 'scheduledStart') ||
          getJobDateKey(a, 'scheduledEnd');
        const bDateKey =
          getJobDateKey(b, 'scheduledStart') ||
          getJobDateKey(b, 'scheduledEnd');

        // Unscheduled jobs (no date key) come first
        if (!aDateKey && bDateKey) {
          return -1;
        }
        if (aDateKey && !bDateKey) {
          return 1;
        }
        // If both have date keys, sort lexicographically (YYYY-MM-DD format sorts correctly)
        if (aDateKey && bDateKey) {
          return aDateKey.localeCompare(bDateKey);
        }
        // If neither has date, maintain original order
        return 0;
      });
  }, [jobs]);

  // Show skeleton when explicitly requested (initial load or background loading)
  if (showSkeleton) {
    return <JobsTabSkeleton />;
  }

  // Show loading text only when fetching and no jobs available (fallback)
  if (fetching && jobs.length === 0) {
    return (
      <div className='flex items-center justify-center p-8'>
        <div className='text-gray-500'>Loading jobs...</div>
      </div>
    );
  }

  // Show no results message when not fetching and no filtered jobs found
  // Check sortedJobs.length instead of jobs.length to account for filtering
  if (!fetching && sortedJobs.length === 0) {
    return (
      <div className='flex items-center justify-center p-8'>
        <div className='text-center'>
          <div className='mb-2 text-gray-500'>No jobs found</div>
          <div className='text-sm text-gray-400'>
            Jobs for this asset will appear here when created
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='py-3'>
      {sortedJobs?.map((job, i) => {
        const currentDateKey =
          getJobDateKey(job, 'scheduledStart') ||
          getJobDateKey(job, 'scheduledEnd');
        const previousJob = i > 0 ? sortedJobs[i - 1] : null;
        const prevDateKey =
          previousJob &&
          (getJobDateKey(previousJob, 'scheduledStart') ||
            getJobDateKey(previousJob, 'scheduledEnd'));

        const isNewDatedGroup =
          !!currentDateKey && currentDateKey !== prevDateKey;
        const isTransitionToUnscheduled =
          !currentDateKey &&
          !job._dateScheduled &&
          !!previousJob &&
          !!previousJob._dateScheduled;
        const isFirstUnscheduled =
          i === 0 && !currentDateKey && !job._dateScheduled;

        return (
          <Fragment key={job.id}>
            {isNewDatedGroup && <DateDisplay date={currentDateKey} job={job} />}
            {(isTransitionToUnscheduled || isFirstUnscheduled) && (
              <DateDisplay date='UNSCHEDULED' />
            )}
            <JobCardSm
              key={job.id}
              job={job}
              isHighlighted={highlightedJobId === job.id}
              onHighlightedClick={onHighlightedJobClick}
              ref={(el: HTMLDivElement | null) => {
                if (el) {
                  jobRefsMap.current.set(job.id, el);
                } else {
                  jobRefsMap.current.delete(job.id);
                }
              }}
            />
          </Fragment>
        );
      })}
    </div>
  );
}

// Cancellable statuses: Draft (Created) and Confirmed (Accepted)
const CANCELLABLE_STATUSES: (Status | JobStatus)[] = [
  JobStatus.Created,
  JobStatus.Accepted,
];

const JobCardSm = forwardRef<
  HTMLDivElement,
  {
    job: AssetJob;
    isHighlighted: boolean;
    onHighlightedClick?: (jobId: string) => void;
  }
>(({ job, isHighlighted, onHighlightedClick }, ref) => {
  const { t } = useTranslation();
  const { assetId, siteId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const [isHovered, setIsHovered] = useState(false);
  const [isSwiped, setIsSwiped] = useState(false);
  const [showSchedulePopup, setShowSchedulePopup] = useState(false);
  const [showAssignPopup, setShowAssignPopup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [, rescheduleJobs] = useMutation(RescheduleJobsDocument);
  const [, assignJobs] = useMutation(AssignJobsDocument);
  const [{ fetching: cancelFetching }, cancelJob] = useCancelJobMutation();

  // Touch/swipe handling for mobile
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const touchCurrentRef = useRef<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const highlightTimeoutRef = useRef<number | null>(null);
  const SWIPE_X_THRESHOLD = 50; // Minimum swipe distance in pixels
  const SWIPE_Y_MAX = 40; // Maximum vertical movement to consider it a swipe

  // Memoize the active check to prevent recalculation on every render
  // Use route-aware matching to avoid false positives from substring matches
  const isActive = useMemo(() => {
    const pathname = location.pathname;
    return (
      matchPath({ path: '/jobs/:view/:jobId', end: false }, pathname)?.params
        .jobId === job.id ||
      matchPath({ path: '/sites/:siteId/jobs/:jobId', end: false }, pathname)
        ?.params.jobId === job.id ||
      matchPath({ path: '/assets/:assetId/jobs/:jobId', end: false }, pathname)
        ?.params.jobId === job.id
    );
  }, [location.pathname, job.id]);

  // Memoize the job link to preserve tab parameter and use site context when available
  const jobLink = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');

    // If we're in a site context, use site-scoped job route
    if (siteId) {
      const baseUrl = `/sites/${siteId}/jobs/${job.id}`;
      return tabParam ? `${baseUrl}?tab=${tabParam}` : baseUrl;
    }

    // If we're in an asset context, use asset-scoped job route
    if (assetId) {
      const baseUrl = `/assets/${assetId}/jobs/${job.id}`;
      return tabParam ? `${baseUrl}?tab=${tabParam}` : baseUrl;
    }

    // If we're in a task context (e.g., /jobs/scheduled/3262/tasks/13508),
    // navigate directly to the job without asset/site context
    // This handles cases where AssetView is opened from a job task
    return `/jobs/${job.id}`;
  }, [assetId, siteId, job.id, location.search]);

  // Build a returnTo state so job close returns back to the asset tab view
  // CRITICAL FIX: When navigating between jobs, preserve the original asset view path
  // instead of creating a chain of job-to-job navigation
  const linkState = useMemo(() => {
    const path = location.pathname + location.search;
    const isAssetContext = path.includes('/assets/');

    if (!isAssetContext) {
      return undefined;
    }

    // If we're already in a job view (navigating from one job to another),
    // use the returnTo from the current location state to preserve the original asset path
    const currentState = location.state as { returnTo?: string } | null;
    if (currentState?.returnTo && path.includes('/jobs/')) {
      return { returnTo: currentState.returnTo };
    }

    // If we're in a job view but don't have returnTo in state,
    // extract the asset path from the current pathname
    if (path.includes('/jobs/')) {
      const assetMatch = path.match(
        /^(\/assets\/[^/]+)(?:\/jobs\/.*)?(\?.*)?$/
      );
      if (assetMatch) {
        const assetPath = assetMatch[1];
        const searchParams = assetMatch[2] || '';
        // Preserve tab parameter if present
        const tabParam = new URLSearchParams(location.search).get('tab');
        const finalSearch = tabParam ? `?tab=${tabParam}` : searchParams;
        return { returnTo: assetPath + finalSearch };
      }
    }

    // Default: use current path (when navigating from asset view directly)
    return { returnTo: path };
  }, [location.pathname, location.search, location.state]);

  const handleScheduleClick = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setShowSchedulePopup(true);
    },
    []
  );

  const handleDeleteClick = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setShowDeleteConfirm(true);
    },
    []
  );

  const handleAssignClick = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setShowAssignPopup(true);
    },
    []
  );

  // Touch handlers for swipe gesture on mobile
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile) return;
      const touch = e.touches[0];
      if (touch) {
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
        touchCurrentRef.current = { x: touch.clientX, y: touch.clientY };
      }
    },
    [isMobile]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile || !touchStartRef.current || !touchCurrentRef.current) {
        touchStartRef.current = null;
        touchCurrentRef.current = null;
        return;
      }

      const dx = touchCurrentRef.current.x - touchStartRef.current.x;
      const dy = Math.abs(touchCurrentRef.current.y - touchStartRef.current.y);

      // Reset touch refs
      touchStartRef.current = null;
      touchCurrentRef.current = null;

      // Check if it's a horizontal swipe (not too much vertical movement)
      if (dy > SWIPE_Y_MAX) {
        // Too much vertical movement, likely scrolling - don't toggle swipe
        return;
      }

      // Swipe left - reveal buttons
      if (dx < -SWIPE_X_THRESHOLD) {
        setIsSwiped(true);
        // Prevent card navigation on swipe
        e.preventDefault();
        e.stopPropagation();
      }
      // Swipe right - hide buttons
      else if (dx > SWIPE_X_THRESHOLD) {
        setIsSwiped(false);
        // Prevent card navigation on swipe
        e.preventDefault();
        e.stopPropagation();
      }
      // Small movement or tap - allow normal click behavior
      else if (Math.abs(dx) < 10) {
        // Very small movement - treat as tap, allow normal navigation
      }
    },
    [isMobile]
  );

  // Use native event listener for touchmove with { passive: false } to enable preventDefault
  useEffect(() => {
    if (!isMobile || !containerRef.current) return;

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartRef.current) return;
      const touch = e.touches[0];
      if (touch && touchStartRef.current) {
        touchCurrentRef.current = { x: touch.clientX, y: touch.clientY };

        // Prevent scrolling if this is a horizontal swipe
        const dx = Math.abs(touch.clientX - touchStartRef.current.x);
        const dy = Math.abs(touch.clientY - touchStartRef.current.y);

        // If horizontal movement is greater than vertical, prevent default to avoid container scrolling
        // This allows vertical scrolling but prevents horizontal scrolling
        if (dx > dy && dx > 10) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    const element = containerRef.current;
    element.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      element.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isMobile]);

  // Handle click outside to close swiped buttons on mobile
  useEffect(() => {
    if (!isMobile || !isSwiped) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      const cardElement = containerRef.current;

      // Don't close if clicking on action buttons or the card itself
      if (
        cardElement &&
        (cardElement.contains(target) ||
          target.closest('button') ||
          target.closest('[role="button"]'))
      ) {
        return;
      }

      setIsSwiped(false);
    };

    // Use a small delay to avoid immediate closure when touch ends
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
      document.addEventListener('touchend', handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('touchend', handleClickOutside, true);
    };
  }, [isMobile, isSwiped]);

  // Cleanup timeout on unmount or job change
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current !== null) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
    };
  }, [job?.id]);

  // Handle click on highlighted job - clear highlight after 3 seconds
  // Defined before early return to satisfy React Hooks rules
  const handleCardClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Only handle if clicking on the card itself, not action buttons
      const target = e.target as HTMLElement;
      const isActionButton =
        target.closest('button') || target.closest('[role="button"]');

      if (!isActionButton && isHighlighted && onHighlightedClick && job?.id) {
        // Clear any existing timeout before setting a new one
        if (highlightTimeoutRef.current !== null) {
          clearTimeout(highlightTimeoutRef.current);
          highlightTimeoutRef.current = null;
        }
        // Clear highlight after 3 seconds delay
        highlightTimeoutRef.current = window.setTimeout(() => {
          if (onHighlightedClick) {
            onHighlightedClick(job.id);
          }
          highlightTimeoutRef.current = null;
        }, 3000);
      }
    },
    [isHighlighted, onHighlightedClick, job?.id]
  );

  // Merge forwarded ref with container ref
  // Defined before early return to satisfy React Hooks rules
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref]
  );

  if (!job) {
    return <Loading />;
  }

  const isUnscheduled =
    !getJobDateKey(job, 'scheduledStart') &&
    !getJobDateKey(job, 'scheduledEnd');

  const hasNoAssignee = !job.assignee;
  const isCancellable = CANCELLABLE_STATUSES.includes(job.status);

  // Show action buttons if any action is available
  const hasActions = isUnscheduled || hasNoAssignee || isCancellable;

  // Calculate how many buttons will be shown to determine margin offset
  const buttonCount =
    (isUnscheduled ? 1 : 0) + (hasNoAssignee ? 1 : 0) + (isCancellable ? 1 : 0);

  // Calculate margin offset based on number of visible buttons (each button ~40px)
  const marginOffset = buttonCount * 40;

  // Determine if buttons should be visible
  // On desktop: show on hover, on mobile: show on swipe
  const shouldShowButtons = isMobile ? isSwiped : isHovered;

  return (
    <div
      ref={setRefs}
      className='group relative touch-pan-y'
      style={{ touchAction: 'pan-y' }}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={handleCardClick}
    >
      <motion.div
        animate={{
          marginRight: shouldShowButtons && hasActions ? marginOffset + 12 : 12,
        }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
      >
        <CardSmall
          linkTo={jobLink}
          linkState={linkState}
          titleText={job.name}
          secondaryText={job.location?.name}
          heroElement={<HeroElement job={job} isActive={isActive} />}
          user={{ name: job.assignee?.name ?? 'U' }}
          status={job.status}
          isActive={isActive}
          isHighlighted={isHighlighted}
        />
      </motion.div>
      {hasActions && (
        <motion.div
          className='absolute right-3 top-0 flex h-full items-center gap-1'
          initial={{ opacity: 0, x: 10 }}
          animate={{
            opacity: shouldShowButtons ? 0.85 : 0,
            x: shouldShowButtons ? 0 : 10,
          }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
        >
          {isUnscheduled && (
            <IconButton
              icon={faCalendar as IconDefinition}
              onClick={handleScheduleClick}
              variant='default'
              size='sm'
              ariaLabel='Schedule job'
              tooltip={!isMobile ? 'Schedule job' : undefined}
            />
          )}
          {hasNoAssignee && (
            <IconButton
              icon={faUserPlus as IconDefinition}
              onClick={handleAssignClick}
              variant='default'
              size='sm'
              className='[&_svg]:h-[22px] [&_svg]:w-[22px]'
              ariaLabel='Assign job'
              tooltip={!isMobile ? 'Assign job' : undefined}
            />
          )}
          {isCancellable && (
            <IconButton
              icon={faTrashCan as IconDefinition}
              onClick={handleDeleteClick}
              size='sm'
              variant='default'
              ariaLabel='Cancel job'
              tooltip={!isMobile ? 'Cancel job' : undefined}
            />
          )}
        </motion.div>
      )}
      {showSchedulePopup && (
        <ScheduleJobPopup
          job={job}
          isOpen={showSchedulePopup}
          onClose={() => setShowSchedulePopup(false)}
          onSave={async (data) => {
            try {
              const result = await rescheduleJobs({
                input: {
                  jobIds: [job.id],
                  startDate: data.scheduledStartDate || null,
                  startTime: data.scheduledStartTime || null,
                  endDate: data.scheduledEndDate || null,
                  endTime: data.scheduledEndTime || null,
                },
              });

              // Check for GraphQL errors
              if (result.error) {
                if (process.env.NODE_ENV === 'development') {
                  console.error('Failed to reschedule job:', result.error);
                }
                toast.error('Failed to reschedule job. Please try again.');
                return; // Don't close popup or navigate on error
              }

              // Success - close popup and navigate
              setShowSchedulePopup(false);

              // Navigate to jobs tab and highlight the rescheduled job
              // Preserve existing search params (like siteId context)
              const searchParams = new URLSearchParams(location.search);
              searchParams.set('tab', '1');
              searchParams.set('highlightJob', job.id);

              // Determine the base path - if in site context, navigate to site asset route
              // Otherwise navigate to asset route
              // If we're in a task context (no assetId/siteId), just close the popup
              // The user can navigate manually if needed
              if (siteId && assetId) {
                const basePath = `/sites/${siteId}/assets/${assetId}`;
                navigate(`${basePath}?${searchParams.toString()}`);
              } else if (assetId) {
                const basePath = `/assets/${assetId}`;
                navigate(`${basePath}?${searchParams.toString()}`);
              }
              // If no assetId/siteId (task context), just close the popup
              // No navigation needed as we're already in the correct context
            } catch (error) {
              if (process.env.NODE_ENV === 'development') {
                console.error('Unexpected error rescheduling job:', error);
              }
              toast.error(
                'An unexpected error occurred while rescheduling the job. Please try again.'
              );
              // Don't close popup or navigate on error
            }
          }}
        />
      )}
      {showDeleteConfirm && (
        <Confirm
          title={t('job:cancel.confirm.title')}
          body={t('job:cancel.confirm.body', { jobName: job.name })}
          cancel={t('job:cancel.confirm.keep')}
          confirm={t('job:cancel.confirm.cancel')}
          danger
          isLoading={cancelFetching}
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={async () => {
            try {
              const result = await cancelJob({ id: job.id });

              // Check for GraphQL errors
              if (result.error) {
                if (process.env.NODE_ENV === 'development') {
                  console.error('Failed to cancel job:', result.error);
                }
                toast.error('Failed to cancel job. Please try again.');
                return; // Don't close dialog on error
              }

              // Success - close dialog
              setShowDeleteConfirm(false);
            } catch (error) {
              if (process.env.NODE_ENV === 'development') {
                console.error('Unexpected error cancelling job:', error);
              }
              toast.error(
                'An unexpected error occurred while cancelling the job. Please try again.'
              );
              // Don't close dialog on error
            }
          }}
        />
      )}
      {showAssignPopup && (
        <AssignJobPopup
          isOpen={showAssignPopup}
          onClose={() => setShowAssignPopup(false)}
          onSave={async (assigneeId: string) => {
            try {
              const result = await assignJobs({
                input: {
                  jobIds: [job.id],
                  assigneeId: [assigneeId],
                },
              });

              // Check for GraphQL errors
              if (result.error) {
                if (process.env.NODE_ENV === 'development') {
                  console.error('Failed to assign job:', result.error);
                }
                toast.error('Failed to assign job. Please try again.');
                return; // Don't close popup on error
              }

              // Success - close popup
              setShowAssignPopup(false);
            } catch (error) {
              if (process.env.NODE_ENV === 'development') {
                console.error('Unexpected error assigning job:', error);
              }
              toast.error(
                'An unexpected error occurred while assigning the job. Please try again.'
              );
              // Don't close popup on error
            }
          }}
        />
      )}
    </div>
  );
});

JobCardSm.displayName = 'JobCardSm';

function HeroElement({ job, isActive }: { job: AssetJob; isActive: boolean }) {
  const { t } = useTranslation();
  // Use scheduledStartTime/scheduledEndTime directly (property-local HH:mm strings)
  // Do NOT convert scheduleStart/scheduleEnd ISO strings - they would use browser timezone
  const startTime = job.scheduledStartTime
    ? formatTime(job.scheduledStartTime)
    : null;
  const endTime = job.scheduledEndTime
    ? formatTime(job.scheduledEndTime)
    : null;

  return (
    <div className='flex flex-col justify-start pr-1 text-right leading-tight'>
      {startTime && (
        <p
          className={classNames(
            'truncate text-sm leading-tight text-grey-70 md:text-[15px] ',
            isActive ? 'text-white' : 'font-medium text-grey-70'
          )}
        >
          {startTime}
        </p>
      )}
      {endTime && (
        <p className='whitespace-nowrap text-xs text-grey-50'>
          {t('job:schedule.to', { time: endTime })}
        </p>
      )}
    </div>
  );
}

type ScheduleJobPopupProps = {
  job: AssetJob;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    scheduledStartDate: string;
    scheduledStartTime: string;
    scheduledEndDate: string;
    scheduledEndTime: string;
  }) => Promise<void>;
};

const ScheduleJobPopup = ({
  job,
  isOpen,
  onClose,
  onSave,
}: ScheduleJobPopupProps) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);

  const {
    control,
    register,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm({
    defaultValues: {
      scheduledStartDate: job.scheduledStartDate || '',
      scheduledStartTime: job.scheduledStartTime || '',
      scheduledEndDate: job.scheduledEndDate || '',
      scheduledEndTime: job.scheduledEndTime || '',
    },
  });

  const validateDateTimeRange = (
    startDate: string,
    startTime: string,
    endDate: string,
    endTime: string
  ): boolean => {
    // Clear any existing errors first
    clearErrors('scheduledEndDate');
    clearErrors('scheduledEndTime');

    // If no end date/time is provided, validation passes (optional field)
    if (!endDate && !endTime) {
      return true;
    }

    // If start date/time is not provided but end is, that's invalid
    if (!startDate && !startTime) {
      setError('scheduledEndDate', {
        type: 'manual',
        message: 'Start date/time must be set before end date/time',
      });
      return false;
    }

    // Combine date and time strings for comparison
    // Date format: YYYY-MM-DD, Time format: HH:mm
    const startDateTime = startDate
      ? `${startDate}T${startTime || '00:00'}:00`
      : null;
    const endDateTime = endDate ? `${endDate}T${endTime || '00:00'}:00` : null;

    if (!startDateTime || !endDateTime) {
      return true; // If either is incomplete, skip validation
    }

    const start = new Date(startDateTime);
    const end = new Date(endDateTime);

    // Check if end is before or equal to start
    if (end <= start) {
      setError('scheduledEndDate', {
        type: 'manual',
        message: 'End date/time must be after start date/time',
      });
      setError('scheduledEndTime', {
        type: 'manual',
        message: 'End date/time must be after start date/time',
      });
      return false;
    }

    return true;
  };

  const onSubmit = async (data: {
    scheduledStartDate: string;
    scheduledStartTime: string;
    scheduledEndDate: string;
    scheduledEndTime: string;
  }) => {
    // Validate date/time range before submitting
    if (
      !validateDateTimeRange(
        data.scheduledStartDate,
        data.scheduledStartTime,
        data.scheduledEndDate,
        data.scheduledEndTime
      )
    ) {
      return; // Prevent submission if validation fails
    }

    setIsSaving(true);
    try {
      await onSave(data);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PopupDialog isOpen={isOpen} onClose={onClose} title='Schedule Job'>
      <div className='p-6'>
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
          <div className='space-y-4'>
            <div className='flex flex-wrap gap-x-12 gap-y-1'>
              <div
                className={classNames(
                  'max-w-md flex-1 pr-1 text-left',
                  watch('scheduledStartDate') || watch('scheduledStartTime')
                    ? 'grid grid-cols-[3fr_2fr_auto] gap-3'
                    : 'grid grid-cols-[3fr_2fr] gap-3'
                )}
              >
                <Input
                  {...register('scheduledStartDate', {
                    onChange: () => {
                      // Clear errors when user modifies start date
                      if (errors.scheduledEndDate || errors.scheduledEndTime) {
                        clearErrors('scheduledEndDate');
                        clearErrors('scheduledEndTime');
                      }
                    },
                  })}
                  label={t('job:form.scheduleStart')}
                  type='date'
                  error={errors.scheduledStartDate?.message}
                />
                <div>
                  <Controller
                    control={control}
                    name='scheduledStartTime'
                    render={({ field: { value, onChange } }) => (
                      <TimePicker
                        label='Time'
                        focusValue='09:00'
                        value={value}
                        onChange={(newValue) => {
                          onChange(newValue);
                          // Clear errors when user modifies start time
                          if (
                            errors.scheduledEndDate ||
                            errors.scheduledEndTime
                          ) {
                            clearErrors('scheduledEndDate');
                            clearErrors('scheduledEndTime');
                          }
                        }}
                        error={errors.scheduledStartTime?.message}
                      />
                    )}
                  />
                </div>
                {(watch('scheduledStartDate') ||
                  watch('scheduledStartTime')) && (
                  <button
                    type='button'
                    className='justify-self-end pt-2.5'
                    onClick={() => {
                      setValue('scheduledStartDate', '');
                      setValue('scheduledStartTime', '');
                      clearErrors('scheduledEndDate');
                      clearErrors('scheduledEndTime');
                    }}
                  >
                    <FontAwesomeIcon
                      className='h-5 w-5 text-brand transition-colors duration-300 hover:text-brand-darker'
                      icon={faCircleXmark}
                    />
                  </button>
                )}
              </div>
              <div
                className={classNames(
                  'max-w-md flex-1 pr-1 text-left',
                  watch('scheduledEndDate') || watch('scheduledEndTime')
                    ? 'grid grid-cols-[3fr_2fr_auto] gap-3'
                    : 'grid grid-cols-[3fr_2fr] gap-3'
                )}
              >
                <Input
                  {...register('scheduledEndDate', {
                    onChange: () => {
                      // Clear errors when user modifies end date
                      clearErrors('scheduledEndDate');
                      clearErrors('scheduledEndTime');
                    },
                  })}
                  label={t('job:form.scheduleEnd')}
                  type='date'
                  error={errors.scheduledEndDate?.message}
                />
                <div>
                  <Controller
                    control={control}
                    name='scheduledEndTime'
                    render={({ field: { value, onChange } }) => (
                      <TimePicker
                        label='Time'
                        focusValue={getDefaultScheduledEndTime(
                          watch('scheduledStartTime')
                        )}
                        value={value}
                        onChange={(newValue) => {
                          onChange(newValue);
                          // Clear errors when user modifies end time
                          clearErrors('scheduledEndDate');
                          clearErrors('scheduledEndTime');
                        }}
                        error={errors.scheduledEndTime?.message}
                      />
                    )}
                  />
                </div>
                {(watch('scheduledEndDate') || watch('scheduledEndTime')) && (
                  <button
                    type='button'
                    className='justify-self-end pt-2.5'
                    onClick={() => {
                      setValue('scheduledEndDate', '');
                      setValue('scheduledEndTime', '');
                    }}
                  >
                    <FontAwesomeIcon
                      className='h-5 w-5 text-brand transition-colors duration-300 hover:text-brand-darker'
                      icon={faCircleXmark}
                    />
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className='flex justify-end gap-3 border-t pt-4'>
            <button
              type='button'
              onClick={onClose}
              className='px-4 py-2 text-sm font-medium uppercase text-grey-70 transition-colors duration-200 hover:text-brand'
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='rounded-md bg-brand px-4 py-2 text-sm font-medium uppercase text-white transition-colors duration-200 hover:bg-brand-darker disabled:opacity-50'
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </PopupDialog>
  );
};

type AssignJobPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (assigneeId: string) => Promise<void>;
};

const AssignJobPopup = ({ isOpen, onClose, onSave }: AssignJobPopupProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact[]>([]);

  const handleSave = async () => {
    if (selectedContact.length === 0) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(selectedContact[0].id);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PopupDialog isOpen={isOpen} onClose={onClose} title='Assign Job'>
      <div className='p-6'>
        <div className='space-y-6'>
          <div className='min-w-[300px]'>
            <FindAddContact
              label='Assignee'
              value={selectedContact}
              onChange={setSelectedContact}
              isTeamMember
              multiple={false}
              optionsBoxHeight='max-h-[200px]'
            />
          </div>
          <div className='flex justify-end gap-3 border-t pt-4'>
            <button
              type='button'
              onClick={onClose}
              className='px-4 py-2 text-sm font-medium uppercase text-grey-70 transition-colors duration-200 hover:text-brand'
              disabled={isSaving}
              aria-label='Cancel assignment'
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={handleSave}
              className='rounded-md bg-brand px-4 py-2 text-sm font-medium uppercase text-white transition-colors duration-200 hover:bg-brand-darker disabled:opacity-50'
              disabled={isSaving || selectedContact.length === 0}
              aria-label='Assign job'
            >
              {isSaving ? 'Assigning...' : 'Assign'}
            </button>
          </div>
        </div>
      </div>
    </PopupDialog>
  );
};
